import type { ConfirmationRequest } from "@adea/actions/spec";
import { lazy, Suspense, useEffect, useState } from "react";

/**
 * The gate asked for a person's confirmation (a risky action, or a change after a run read untrusted content).
 * `askToConfirm` shows the dialog and resolves with the token the person's click earned, or null if they
 * cancelled. The action client calls it; a screen never does. Mount <ConfirmHost/> once, in the app layout.
 * The dialog itself (`confirm-dialog-view.tsx`) is fetched the first time one is asked for.
 */

export type Pending = {
  request: ConfirmationRequest;
  confirm: (typed?: string) => Promise<string>;
  resolve: (token: string | null) => void;
};

let show: ((p: Pending) => void) | null = null;

export function askToConfirm(
  request: ConfirmationRequest,
  confirm: (typed?: string) => Promise<string>,
): Promise<string | null> {
  return new Promise((resolve) => {
    if (!show) {
      resolve(null);
      return;
    }
    show({ request, confirm, resolve });
  });
}

const ConfirmDialogView = lazy(() => import("./confirm-dialog-view.tsx"));

export function ConfirmHost() {
  const [pending, setPending] = useState<Pending | null>(null);
  const [asked, setAsked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    show = (p) => {
      setError(null);
      setBusy(false);
      setPending(p);
      setAsked(true);
    };
    return () => {
      show = null;
    };
  }, []);
  if (!asked) return null;
  return (
    <Suspense fallback={null}>
      <ConfirmDialogView
        pending={pending}
        busy={busy}
        error={error}
        setBusy={setBusy}
        setError={setError}
        onDone={() => setPending(null)}
      />
    </Suspense>
  );
}
